"use client"

import { useEffect, useState } from "react"
import "./LoginWithPin.css"
import { Link, useNavigate } from "react-router-dom"
import axios from "axios"
import Cookies from "js-cookie"
import { ToastContainer, toast } from "react-toastify"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import Logo from "../img/logo3.png"

const BRAND_FEATURES = [
  { icon: "👥", label: "Manage all your gym members" },
  { icon: "💳", label: "Track payments & revenue" },
  { icon: "📊", label: "Analytics & booking dashboard" },
  { icon: "📱", label: "QR code member check-ins" },
]

const LoginWithPin = () => {
  const navigate = useNavigate()
  const [loginData, setLoginData] = useState({ email: "", password: "" })
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const loginId = Cookies.get("auth")
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/verify-token`,
          {},
          { headers: { Authorization: loginId } }
        )
        if (response.data.message === "Token is valid") navigate("/profile")
      } catch (error) {
        console.error("Error verifying token:", error.response?.status)
        Cookies.remove("auth")
      }
    }
    if (loginId) verifyToken()
  }, [loginId, navigate])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setLoginData({ ...loginData, [name]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/login`, {
        email: loginData.email.toLowerCase(),
        password: loginData.password,
      })
      const { token } = response.data
      document.cookie = `auth=${token}; path=/;`
      toast.success("Login successful!")
      setTimeout(() => navigate("/profile"), 1000)
    } catch (error) {
      toast.error("Invalid email or password. Please try again.")
      if (error.response?.status === 403) navigate("/verify-email")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="login-page">
      {/* Minimal top nav */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <Link to="/" className="lp-logo">
            <img src={Logo} alt="Yupluck" />
            <span>Yupluck</span>
          </Link>
          <Link to="/register" className="lp-nav-link">
            Don't have an account? <strong style={{ color: "#10b981" }}>Register free →</strong>
          </Link>
        </div>
      </nav>

      <div className="login-body">
        {/* Left brand panel */}
        <div className="login-brand">
          <div className="login-brand-inner">
            <div className="lp-brand-logo">
              <img src={Logo} alt="Yupluck" />
              <span>Yupluck</span>
            </div>
            <h2>The smarter way to run your gym</h2>
            <p>
              Manage members, bookings, equipment, and payments — all from one
              powerful, completely free platform.
            </p>
            <div className="lp-features">
              {BRAND_FEATURES.map((f, i) => (
                <div className="lp-feature" key={i}>
                  <div className="lp-feature-icon">{f.icon}</div>
                  <span>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right form panel */}
        <div className="login-form-panel">
          <div className="login-card">
            <div className="login-card-header">
              <h1>Welcome back</h1>
              <p>Sign in to your gym dashboard</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={loginData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="you@example.com"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={loginData.password}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your password"
                    className="form-control"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-options">
                <button
                  type="button"
                  className="forgot-password"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot password?
                </button>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button
                type="submit"
                className={`btn-login ${isLoading ? "loading" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign In →"}
              </button>

              <div className="divider"><span>or</span></div>

              <button
                type="button"
                className="btn-register"
                onClick={() => navigate("/register")}
              >
                Create new account
              </button>
            </form>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        theme="dark"
      />
    </div>
  )
}

export default LoginWithPin
